// src/components/version-info-button.tsx
import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Popover, Typography, Box, Button } from '@material-ui/core';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCustomizations, getApiBaseUrl } from '@personicom/customizations';
import { getUatDiagnosticsContext, getUatDiagnosticHistory } from 'helpers/uat-diagnostics';
import { copyTextToClipboard } from 'helpers/general-helpers';

const buildStyles = makeStyles((theme) => ({
  infoButton: {
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  popoverContent: {
    padding: theme.spacing(2),
    minWidth: 450,
  },
  infoRow: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    minWidth: 140,
  },
  value: {
    fontFamily: 'monospace',
    fontSize: '0.9em',
    wordBreak: 'break-all',
  },
  diagnosticsActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  diagnosticsStatus: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
}));

/**
 * VersionInfoButton component
 * Displays a floating info button in the bottom-left corner that shows:
 * - Application version, build number, and commit hash
 * - Customizations package version and build date
 * - SWA Environment
 * - API Base URL
 */
const VersionInfoButton: React.FC = () => {
  const classes = buildStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [copyStatus, setCopyStatus] = useState<string>('');
  const { assetEnvironment } = useCustomizations();
  const diagnosticsContext = useMemo(() => getUatDiagnosticsContext(), []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCopyStatus('');
  };

  const open = Boolean(anchorEl);
  const id = open ? 'version-info-popover' : undefined;

  // Resolve API base URL dynamically when popover opens to ensure we get the runtime meta tag value
  // (SWA injects meta tags at runtime, not build time)
  const apiBaseUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return import.meta.env.VITE_API_APP_BASE_URL || 'Not configured';
    }

    try {
      return getApiBaseUrl();
    } catch (error) {
      console.warn('VersionInfoButton: Failed to resolve API base URL via runtime helpers. Falling back to build-time env.', error);
      return import.meta.env.VITE_API_APP_BASE_URL || 'Not configured';
    }
  }, [open]); // Recalculate when popover opens to get fresh meta tag value

  // Get customizations package version - hardcoded to match package.json
  // TODO: Consider dynamically importing package version if needed
  const customizationsVersion = '4.8.34';

  const handleCopyUatDiagnostics = async () => {
    const diagnostics = getUatDiagnosticHistory();
    const exportPayload = {
      exportedAtUtc: new Date().toISOString(),
      location: typeof window !== 'undefined' ? window.location.href : 'unknown',
      versions: {
        reportingUi: {
          version: __APP_VERSION__,
          build: __BUILD_NUMBER__,
          commit: __COMMIT_HASH__.substring(0, 7),
        },
        customizations: customizationsVersion,
      },
      environment: assetEnvironment || 'Not configured',
      apiBaseUrl,
      diagnosticsEnabled: diagnosticsContext.enabled,
      diagnosticsCount: diagnostics.length,
      diagnostics,
    };

    const success = await copyTextToClipboard(JSON.stringify(exportPayload, null, 2));
    setCopyStatus(success ? `Copied ${diagnostics.length} diagnostic events` : 'Copy failed');
  };

  return (
    <>
      <IconButton
        className={classes.infoButton}
        size="small"
        onClick={handleClick}
        aria-describedby={id}
        title="Version Information"
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box className={classes.popoverContent}>
          <Typography variant="h6" gutterBottom>
            Version Information
          </Typography>
          
          <Box className={classes.infoRow}>
            <Typography variant="body2" component="span" className={classes.label}>
              Reporting UI:
            </Typography>
            <Typography variant="body2" component="span" className={classes.value}>
              v{__APP_VERSION__} (Build: {__BUILD_NUMBER__}, Commit: {__COMMIT_HASH__.substring(0, 7)})
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography variant="body2" component="span" className={classes.label}>
              Customizations:
            </Typography>
            <Typography variant="body2" component="span" className={classes.value}>
              v{customizationsVersion}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography variant="body2" component="span" className={classes.label}>
              SWA Environment:
            </Typography>
            <Typography variant="body2" component="span" className={classes.value}>
              {assetEnvironment || 'Not configured'}
            </Typography>
          </Box>

	          <Box className={classes.infoRow}>
	            <Typography variant="body2" component="span" className={classes.label}>
	              API Base URL:
            </Typography>
            <Typography variant="body2" component="span" className={classes.value}>
	              {apiBaseUrl}
	            </Typography>
	          </Box>

            {diagnosticsContext.enabled && (
              <Box className={classes.diagnosticsActions}>
                <Button variant="outlined" size="small" onClick={handleCopyUatDiagnostics}>
                  Copy UAT Diagnostics
                </Button>
                <Typography variant="body2" className={classes.diagnosticsStatus}>
                  {copyStatus || `${getUatDiagnosticHistory().length} events captured`}
                </Typography>
              </Box>
            )}
	        </Box>
	      </Popover>
	    </>
  );
};

export default VersionInfoButton;
